// OpenHomeDetailView.tsx
import React, { useState, useEffect } from "react";
import moment from "moment";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import ProtectedComponent from "../../../../../components/ProtectedComponent";
import NavigationLayout from "../../../navigation";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./OpenHomeDetailView.scss";
import { PropertyApi } from "../../../../../utils/api/Property";
import utc from "dayjs/plugin/utc";
import { CommonEnums } from "../../../../../enums/common.enums";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import OpenHomeConfigurePost from "./OpenHomeConfigurePost";
import Modal from "@mui/material/Modal";
import toast from "react-hot-toast";
import DownArrowIcon from "../../../../../components/icons/DownArrowIcon";
import { CircularProgress, TextField } from "@mui/material";
import { clientRoutes } from "../../../../../utils/routes";
import Loading from "../../../../loading/Loading";
import { useDispatch } from "react-redux";
import OpenHomeListTopButtonsAndSearch from "./OpenHomeListTopButtonsAndSearch";
import OpneHomeviewMenuIcon from "./OpneHomeviewMenuIcon";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import PreviewModal from "../../propertypost/view/PreviewModal";
import { getAgentProfileImageUrl, getImageUrl, resolvePropertyVariantImageUrl } from "../../../../../utils/helpers/common";
import PostAssets from "./PostAssets";

dayjs.extend(utc);

const OpenHomeDetailView = () => {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openHomeDate, setOpenHomeDate] = useState<dayjs.Dayjs | null>(null);
  const [createdOn, setCreatedOn] = useState(null);
  const [updatedOn, setUpdatedOn] = useState(null);
  const [postStatus, setPostStatus] = useState<string | null>(null);
  const [listingType, setListingType] = useState<string>("");
  const [postType, setPostType] = useState<string | null>(null);
  const [scheduleDatetime, setScheduleDatetime] = useState<string | null>(null);
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [availableProperties, setAvailableProperties] = useState<any[]>([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const BASE_URL = process.env.REACT_APP_API_ENDPOINT;
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("open_home_info");
  const [timeTo, setTimeTo] = useState("00:00");
  const [showScheduleForm, setShowScheduleForm] = useState(true);
  const [date, setDate] = useState("");
  const [selectedButton, setSelectedButton] = useState("schedule");
  const [localPostStatus, setLocalPostStatus] = useState<string | null>(null);
  const [openHomePost, setOpenHomePost] = useState<any>(null);

  const fetchSchedule = async () => {
    if (!scheduleId) return;

    setLoading(true);
    try {
      const res = await PropertyApi.getOpenHomeScheduleById(scheduleId);

      if (res?.data?.success && res.data.data) {
        const schedule = res.data.data;

        setOpenHomePost(schedule);

        const {
          open_home_date,
          properties: apiProps,
          post_status,
          createdAt,
          updatedAt,
          listing_type,
          post_type,
          schedule_datetime,
        } = res.data.data;

        let selectedActivity: any = null;
        if (post_type === "image_post") {
          selectedActivity = schedule?.image_post_design_activity_ref;
        } else if (post_type === "story_post") {
          selectedActivity = schedule?.story_post_design_activity_ref;
        } else if (post_type === "video_post") {
          selectedActivity = schedule?.video_post_design_activity_ref;
        } else if (post_type === "reel_post") {
          selectedActivity = schedule?.reel_post_design_activity_ref;
        }

        const images: string[] = selectedActivity?.design_task_id
          ?.output_image_path
          ? [
              CommonHelpers.getImageUrlFromImageTool({
                path: selectedActivity.design_task_id.output_image_path,
              }),
            ]
          : [];

        setPreviewImages(images);

        setOpenHomeDate(dayjs.utc(open_home_date));
        setCreatedOn(createdAt);
        setUpdatedOn(updatedAt);
        setPostStatus(post_status);
        setListingType(listing_type);
        setPostType(post_type || null);
        setScheduleDatetime(schedule_datetime || null);

        const mapped = apiProps
          .map((p: any) => {
            const hasThumbnail =
              !!p.property_details?.thumbnail_image_ref?.compressed_file_path;
            if (!hasThumbnail) {
              return null;
            }

            return {
              id: p._id,
              propertyId: p.property_id,
              file: `${BASE_URL}/${p.property_details.thumbnail_image_ref.compressed_file_path}`,
              tags: [
                p.property_details?.property_type_ref?.name,
                p.property_details?.listing_type?.name,
                p.property_details?.property_stage_ref?.name,
              ].filter(Boolean),
              address: [
                p.property_details?.property_no,
                p.property_details?.property_no_and_street_name,
                p.property_details?.city,
                p.property_details?.state,
                p.property_details?.country,
                p.property_details?.post_code,
              ]
                .filter(Boolean)
                .join(", "),
              timeFrom: p.open_home_start_time
                ? dayjs(p.open_home_start_time)
                : null,
              timeTo: p.open_home_end_time ? dayjs(p.open_home_end_time) : null,
              price: p.property_details?.start_price
                ? `$${p.property_details.start_price} - $${p.property_details.end_price}`
                : "",
              listed: p.property_details?.createdAt
                ? moment(p.property_details.createdAt).format("DD-MM-YYYY")
                : "",
            };
          })
          .filter(Boolean);
        setProperties(mapped);
      }
    } catch (err) {
      console.error("Error fetching schedule:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSchedule();
  }, [scheduleId]);

  const fetchProperties = async (search: string = searchTerm) => {
    setIsLoadingModal(true);
    try {
      const res = await PropertyApi.getPostProperties({
        page: 1,
        search,
        listing_type_id: "",
        agents_id: "",
        property_stage_id: "",
        property_type_id: "",
        start_date: "",
        end_date: "",
      });

      const docs = res?.data?.data?.docs;
      const filteredDocs = Array.isArray(docs)
        ? docs.filter(
            (doc) =>
              !properties.some((p) => String(p.propertyId) === String(doc._id))
          )
        : [];

      setAvailableProperties(filteredDocs);
    } catch (err) {
      console.error("Failed to fetch properties", err);
      setAvailableProperties([]);
    } finally {
      setIsLoadingModal(false);
    }
  };

  useEffect(() => {
    if (openHomePost?._id) {
      const currentParams: Record<string, string> = {};

      searchParams.forEach((value, key) => {
        currentParams[key] = value;
      });

      setSearchParams({
        ...currentParams,
        post_type: openHomePost.post_type || "",
        // subscription_type: propertyPost.subscription_type || "",
      });
    }
  }, [openHomePost, searchParams, setSearchParams]);

  const handleOpenModal = async () => {
    setModalOpen(true);
    await fetchProperties();
  };

  const togglePropertySelect = (id: string) => {
    setSelectedPropertyIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleAddSelectedProperty = () => {
    const selectedItems = availableProperties.filter((item) =>
      selectedPropertyIds.includes(item._id)
    );

    const newProperties = selectedItems
      .filter((item) => !properties.some((p) => p.id === item._id))
      .map((found) => ({
        id: null,
        propertyId: found._id,
        file: found.thumbnail_image_ref?.compressed_file_path
          ? `${BASE_URL}/${found.thumbnail_image_ref.compressed_file_path}`
          : "/images/image-video-generation-error-fallback-image-for-card.png",
        tags: [
          found.property_type_ref?.name,
          found.listing_type?.name,
          found.property_stage_ref?.name,
        ].filter(Boolean),
        address: [
          found.property_no,
          found.property_no_and_street_name,
          found.city,
          found.state,
          found.country,
          found.post_code,
        ]
          .filter(Boolean)
          .join(", "),
        timeFrom: dayjs().hour(0).minute(0),
        timeTo: dayjs().hour(0).minute(0),
        price: found.price,
        listed: moment(found.createdAt).format("DD-MM-YYYY"),
      }));

    setProperties((prev) => [...prev, ...newProperties]);
    setModalOpen(false);
    setSelectedPropertyIds([]);
  };

  const handleRemove = async (openHomePropertyId: string) => {
    try {
      const res = await PropertyApi.deleteOpenHomeProperty(openHomePropertyId);

      if (res?.data?.success) {
        toast.success("Property removed successfully!");
        setProperties((prev) =>
          prev.filter((p) => p.id !== openHomePropertyId)
        );
      } else {
        toast.error(res?.data?.message || "Failed to remove property");
      }
    } catch (err) {
      console.error("Failed to delete property:", err);
      toast.error("Something went wrong while removing property");
    }
  };

  const handleTimeChange = (
    id: string,
    type: "from" | "to",
    value: dayjs.Dayjs | null
  ) => {
    setProperties((prev) =>
      prev.map((property) =>
        property.id === id
          ? {
              ...property,
              timeFrom: type === "from" ? value : property.timeFrom,
              timeTo: type === "to" ? value : property.timeTo,
            }
          : property
      )
    );
  };

  const handleUpdatePostConfiguration = async (): Promise<boolean> => {
    if (!scheduleId) return false;

    try {
      const date = openHomeDate
        ? dayjs(openHomeDate).format("YYYY-MM-DD")
        : null;

      const payload: any = {
        ...(date && { open_home_date: date }),
        ...(listingType && { listing_type: listingType }),
        properties: properties.map((property) => {
          const start = property.timeFrom
            ? dayjs(
                `${date}T${property.timeFrom.format("HH:mm")}`
              ).toISOString()
            : null;

          const end = property.timeTo
            ? dayjs(`${date}T${property.timeTo.format("HH:mm")}`).toISOString()
            : null;

          return {
            ...(property.id && { open_home_property_record_id: property.id }),
            property_id: property.propertyId,
            ...(start && { open_home_start_time: start }),
            ...(end && { open_home_end_time: end }),
          };
        }),
      };

      const res = await PropertyApi.updateOpenHomePost(scheduleId, payload);

      if (res?.data?.success) {
        // toast.success("Open Home Updated Successfully!");
        return true;
      } else {
        toast.error(res?.data?.message || "Failed to update open home");
        return false;
      }
    } catch (err) {
      console.error("Error updating schedule:", err);
      toast.error("Failed to update open home");
      return false;
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return <p className="status-badge completed">Completed</p>;
      case "processing":
        return <p className="status-badge processing">Processing</p>;
      case "pending":
        return <p className="status-badge pending">Pending</p>;
      case "pending_for_social_media_upload":
        return (
          <p className="status-badge pending_for_social_media_upload">
            Pending Upload
          </p>
        );
      case "scheduled for post":
        return <p className="status-badge scheduled">Scheduled</p>;
      case "cancelled":
        return <p className="status-badge cancelled">Cancelled</p>;
      case "errored":
      case "error":
        return <p className="status-badge errored">Error</p>;
      default:
        return <p className="status-badge">N/A</p>;
    }
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="schedule-open-home-container">
          <div
            className="propertie-view-back-btn"
            onClick={() => {
              const subscriptionType = searchParams.get("subscription_type");
              const backUrl = subscriptionType
                ? `${clientRoutes.OpenHomeList}`
                : clientRoutes.OpenHomeList;

              navigate(backUrl);
            }}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to Open Home Schedules</p>
          </div>
          <div className="header-row">
            <h1 className="title">Open Home Info</h1>
            <div className="action-menu-icon-wrapper">
              <OpneHomeviewMenuIcon
                postId={scheduleId ?? ""}
                onUpdated={() => fetchSchedule()}
                onPreview={() => {
                  if (previewImages.length > 0) {
                    setPreviewOpen(true);
                  } else {
                    toast.error("No generated designs yet");
                  }
                }}
                previewImageUrl={previewImages[0]}
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <Loading />
            </div>
          ) : (
            <>
              <div className="auction-top-info">
                <div className="top-row">
                  <div className="info-item">
                    <label>Open Home Id</label>
                    <p>{scheduleId}</p>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    {getStatusBadge(postStatus)}
                  </div>
                  <div className="info-item">
                    <label>Post From</label>
                    <p>Agent</p>
                  </div>
                  <div className="info-item">
                    <label>Agent</label>
                    <p className="agent-name">
                      {openHomePost.agent_ref?.principal_agent_first_name || ""}{" "}
                      {openHomePost.agent_ref?.principal_agent_last_name || ""}
                    </p>
                  </div>
                </div>

                <div className="bottom-row">
                  <div className="info-item">
                    <label>Agency</label>
                    <p className="agent-name">
                      {openHomePost?.agency_ref?.agency_trading_name}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Open Home Date</label>
                    <p>
                      {openHomeDate
                        ? dayjs(openHomeDate).format("hh:mm A, MMMM D, YYYY")
                        : "-"}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Lisiting Type</label>
                    <p>
                      For{" "}
                      {listingType
                        ? listingType.charAt(0).toUpperCase() +
                          listingType.slice(1)
                        : "-"}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Schedule Datetime</label>
                    <p>
                      {scheduleDatetime
                        ? dayjs(scheduleDatetime).format(
                            "hh:mm A, MMMM D, YYYY"
                          )
                        : "-"}
                    </p>
                  </div>
                </div>
                <div className="bottom-row">
                  <div className="info-item">
                    <label>Created On</label>
                    <p>
                      {createdOn
                        ? dayjs(createdOn).format("hh:mm A, MMMM D, YYYY")
                        : "N/A"}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Last Updated On</label>
                    <p>
                      {updatedOn
                        ? dayjs(updatedOn).format("hh:mm A, MMMM D, YYYY")
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* <OpenHomeInfoTabs
                activeTab={step}
                onTabChange={(newStep) =>
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("step", newStep);
                    return next;
                  })
                }
                showPostTypeTab={false}
              /> */}

              <div className="custom-tabs">
                <div
                  className={`tab-item ${
                    activeTab === "open_home_info" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("open_home_info");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "open_home_info",
                    });
                  }}
                >
                  Open Home Info
                </div>

                <div
                  className={`tab-item ${
                    activeTab === "post_assets" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("post_assets");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "post_assets",
                    });
                  }}
                >
                  Post Assets
                </div>

                <div
                  className={`tab-item ${
                    activeTab === "postConfig" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("postConfig");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "configure_post",
                    });
                  }}
                >
                  Post Configuration
                </div>
              </div>

              {activeTab === "open_home_info" && (
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="form-row">
                      <div className="date-input">
                        <label>Open Home Date*</label>
                        <DatePicker
                          value={openHomeDate}
                          onChange={(newValue) => setOpenHomeDate(newValue)}
                          format="DD/MM/YYYY"
                          slotProps={{
                            textField: {
                              sx: {
                                width: "100%",
                                maxWidth: "337px",
                                "& .MuiOutlinedInput-root": {
                                  height: "40px",
                                  paddingRight: "14px",
                                  borderRadius: "8px",
                                },
                                "& fieldset": {
                                  borderColor:
                                    "var(--Input-Border-Primary, #767279)",
                                },
                              },
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div className="select-property-header">
                      <p className="select-property-header-title">
                        Properties*
                      </p>
                      <p className="select-property-header-description">
                        List of properties included in open home
                      </p>
                    </div>

                    <div className="upload-wrapper">
                      {properties.map((property) => (
                        <div key={property.id} className="property-wrapper">
                          <div className="upload-card has-image">
                            <div className="image-preview-wrapper">
                              <img
                                src={property.file}
                                alt="Property"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src =
                                    "/images/image-video-generation-error-fallback-image-for-card.png";
                                }}
                              />

                              <button
                                className="remove-btn"
                                onClick={() => handleRemove(property.id)}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          <div className="property-details">
                            <div className="property-tags">
                              {property.tags?.map((tag: string) => (
                                <span key={tag} className="tag">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <p className="property-address">
                              {property.address}
                            </p>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <label className="time-label">
                                Open Home Time From*
                              </label>
                              <TimePicker
                                value={property.timeFrom}
                                onChange={(val) =>
                                  handleTimeChange(property.id, "from", val)
                                }
                                slotProps={{
                                  textField: {
                                    sx: {
                                      width: "100%",
                                      maxWidth: "337px",
                                      "& .MuiOutlinedInput-root": {
                                        height: "40px",
                                        paddingRight: "14px",
                                        borderRadius: "8px",
                                      },
                                      "& fieldset": {
                                        borderColor:
                                          "var(--Input-Border-Primary, #767279)",
                                      },
                                    },
                                  },
                                }}
                              />

                              <label className="time-label timalable">
                                Open Home Time To*
                              </label>
                              <TimePicker
                                value={property.timeTo}
                                onChange={(val) =>
                                  handleTimeChange(property.id, "to", val)
                                }
                                slotProps={{
                                  textField: {
                                    sx: {
                                      width: "100%",
                                      maxWidth: "337px",
                                      "& .MuiOutlinedInput-root": {
                                        height: "40px",
                                        paddingRight: "14px",
                                        borderRadius: "8px",
                                      },
                                      "& fieldset": {
                                        borderColor:
                                          "var(--Input-Border-Primary, #767279)",
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                      ))}

                      <div
                        className="upload-card add-btn-only"
                        onClick={handleOpenModal}
                      >
                        <span className="plus">+</span>
                        <p>Add Property</p>
                      </div>
                    </div>
                  </LocalizationProvider>

                  <div className="open-home-post-form">
                    <h3 className="section-title">Schedule Date & Time</h3>
                    <p className="section-sub-title">
                      Date and time for the post schedule
                    </p>

                    {localPostStatus === "scheduled" && date && timeTo && (
                      <div className="status-message scheduled">
                        <p>Scheduled for Automatic Posting.</p>
                        <p className="auto-post">
                          Will be published at{" "}
                          {dayjs(timeTo, "HH:mm").format("hh:mm A")},{" "}
                          {dayjs(date).format("DD-MM-YYYY")}.
                        </p>
                      </div>
                    )}

                    {localPostStatus === "published" && date && timeTo && (
                      <div className="status-message published">
                        <p className="auto-post">
                          Post is Completed and published.
                          {dayjs(timeTo, "HH:mm").format("hh:mm A")},
                          {dayjs(date).format("DD-MM-YYYY")}.
                        </p>
                      </div>
                    )}

                    {localPostStatus !== "published" && (
                      <div className="button-group">
                        <button
                          className={`auto-post ${
                            selectedButton === "auto" ? "selected" : ""
                          }`}
                          onClick={() => {
                            if (!date || !timeTo) {
                              toast.error(
                                "Please select both date and time to schedule."
                              );
                              return;
                            }
                            setLocalPostStatus("scheduled");
                            setSelectedButton("auto");
                            setShowScheduleForm(false);
                          }}
                        >
                          Auto Post
                        </button>

                        <button
                          className={`confirm-btn ${
                            selectedButton === "postNow" ? "selected" : ""
                          }`}
                          onClick={() => {
                            if (!date || !timeTo) {
                              toast.error(
                                "Please select both date and time to publish."
                              );
                              return;
                            }
                            setLocalPostStatus("published");
                            setSelectedButton("postNow");
                            setShowScheduleForm(false);
                          }}
                        >
                          Post Now
                        </button>

                        <button
                          className={`schedule-btn ${
                            showScheduleForm || selectedButton === "schedule"
                              ? "active selected"
                              : ""
                          }`}
                          onClick={() => {
                            setShowScheduleForm(!showScheduleForm);
                            setSelectedButton("schedule");
                          }}
                        >
                          Schedule Post
                        </button>
                      </div>
                    )}

                    {showScheduleForm && (
                      <div className="datetime-row">
                        <div className="datetime-field">
                          <label>Date*</label>
                          <div className="input-icon-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={date ? dayjs(date) : null}
                                onChange={(newValue) =>
                                  setDate(
                                    newValue
                                      ? newValue.format("YYYY-MM-DD")
                                      : ""
                                  )
                                }
                                format="DD/MM/YYYY"
                                slots={{ textField: TextField }}
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    size: "small",
                                    fullWidth: false,
                                    InputProps: {
                                      className: "input",
                                    },
                                    sx: {
                                      input: {
                                        color: "#000",
                                        "&::placeholder": {
                                          color: "#333",
                                          opacity: 1,
                                        },
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>

                        <div className="datetime-field">
                          <label>Time*</label>
                          <div className="input-icon-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <TimePicker
                                value={timeTo ? dayjs(timeTo, "HH:mm") : null}
                                onChange={(newValue) =>
                                  setTimeTo(
                                    newValue ? newValue.format("HH:mm") : ""
                                  )
                                }
                                ampm
                                format="hh:mm A"
                                slots={{ textField: TextField }}
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    size: "small",
                                    fullWidth: false,
                                    InputProps: {
                                      className: "input",
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* <div className="action-buttons">
                    <button className="cancel-btn" onClick={() => navigate(-1)}>
                      Cancel
                    </button>
                    <button
                      className="confirm-btn"
                      onClick={async () => {
                        setProcessingModalOpen(true);
                        setIsProcessing(true);

                        try {
                          const success = await handleUpdatePostConfiguration();
                          if (success) {
                            if (success) {
                              await fetchSchedule();
                              toast.success("Open Home updated successfully!");
                            }
                          }
                        } finally {
                          setIsProcessing(false);
                          setProcessingModalOpen(false);
                        }
                      }}
                    >
                      Update
                    </button>
                  </div> */}
                </>
              )}

              {activeTab === "post_assets" && <PostAssets />}

              {activeTab === "postConfig" && (
                <OpenHomeConfigurePost
                  postType={postType}
                  postStatus={postStatus}
                  scheduleDatetime={scheduleDatetime}
                  openHomeDate={openHomeDate}
                  properties={properties}
                  // onUpdatePostConfiguration={handleUpdatePostConfiguration}
                />
              )}
            </>
          )}
          <Modal
            open={modalOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setModalOpen(false);
              }
            }}
            aria-labelledby="add-property-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-content-wrapper">
              {isLoadingModal ? (
                <div className="loading-container">
                  <Loading />
                </div>
              ) : (
                <>
                  <div className="modal-header">
                    <h2 className="modal-heading">Select Property</h2>
                  </div>
                  <OpenHomeListTopButtonsAndSearch
                    add_btn_name="Add Property"
                    add_btn_link=""
                    reset_btn_link=""
                    placeholder="Search properties..."
                    value={searchTerm}
                    onChangeSearch={(value) => {
                      console.log(value);
                      setSearchTerm(value);

                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                      }

                      searchTimeoutRef.current = setTimeout(() => {
                        fetchProperties(value);
                      }, 500);
                    }}
                    type="properties"
                    showAddBtn={false}
                    showResetBtn={false}
                    showFilterBtn={false}
                  />
                  <div className="modal-card-grid">
                    {availableProperties.length === 0 ? (
                      <p>No properties found.</p>
                    ) : (
                      availableProperties.map((item) => {
                        const isSelected = selectedPropertyIds.includes(
                          item._id
                        );
                        // const imageUrl = item?.thumbnail_image_ref
                        //   ?.compressed_file_path
                        //   ? `${BASE_URL}/${item.thumbnail_image_ref.compressed_file_path}`
                        //   : "/images/image-video-generation-error-fallback-image-for-card.png";
                        const imageUrl = item?.thumbnail_image_ref
                          ? resolvePropertyVariantImageUrl(
                              item?.thumbnail_image_ref?.medium_image_url,
                              item?.thumbnail_image_ref?.compressed_file_path,
                            ) ||
                            "/images/image-video-generation-error-fallback-image-for-card.png"
                          : "/images/image-video-generation-error-fallback-image-for-card.png";
                        return (
                          <div
                            key={item._id}
                            className={`modal-property-card ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() => togglePropertySelect(item._id)}
                          >
                            <div
                              className={`modal-image ${
                                isSelected ? "selected" : ""
                              }`}
                            >
                              <img
                                src={imageUrl}
                                alt="Property"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src =
                                    "/images/image-video-generation-error-fallback-image-for-card.png";
                                }}
                              />
                              <div
                                className={`check-overlay ${
                                  isSelected ? "checked" : ""
                                }`}
                              >
                                {isSelected && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M5 13l4 4L19 7"
                                      stroke="#fff"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                              <div className="modal-agent-info">
                                <img
                                  // src={
                                  //   getImageUrl(
                                  //     item?.agent_ref?.profile_image_ref
                                  //       ?.compressed_file_path || ""
                                  //   ) || "/images/profile-placeholder-image.png"
                                  // }
                                  src={
                                    getAgentProfileImageUrl(
                                      item?.agent_ref?.profile_image_ref,
                                    ) ||
                                    getImageUrl(
                                      item?.agent_ref?.profile_image_ref
                                        ?.compressed_file_path || "",
                                    ) ||
                                    "/images/profile-placeholder-image.png"
                                  }
                                  alt="Agent"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src =
                                      "/images/profile-placeholder-image.png";
                                  }}
                                />

                                <div>
                                  <p>
                                    {item.agent_ref
                                      ?.principal_agent_first_name || ""}{" "}
                                    {item.agent_ref
                                      ?.principal_agent_last_name || ""}
                                  </p>
                                  <p>
                                    +
                                    {item.agent_ref
                                      ?.principal_phone_country_code || ""}{" "}
                                    {item.agent_ref
                                      ?.principal_agent_mobile_number || ""}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="modal-property-details">
                              <div className="modal-tags">
                                {item.property_type_ref?.name && (
                                  <span>{item.property_type_ref.name}</span>
                                )}
                                {item.listing_type?.name && (
                                  <span>{item.listing_type.name}</span>
                                )}
                                {item.property_stage_ref?.name && (
                                  <span>{item.property_stage_ref.name}</span>
                                )}
                              </div>
                              <p className="modal-address">
                                {CommonHelpers.getPropertyAddress({
                                  city: item?.city as string,
                                  country: "",
                                  no: item?.property_no as string,
                                  postCode: item?.post_code as string,
                                  state: item?.state as string,
                                  streetName:
                                    item?.property_no_and_street_name as string,
                                })}
                              </p>
                              <div className="modal-price-date">
                                <div className="price">
                                  {(() => {
                                    const type = item.price_type?.slug;
                                    const {
                                      price,
                                      start_price,
                                      end_price,
                                      eoi_datetime,
                                    } = item;
                                    switch (type) {
                                      case "price-range":
                                        return `$${start_price} - $${end_price}`;
                                      case "offer-above-xyz-price":
                                        return `Offer above $${price}`;
                                      case "contact-agent":
                                      case "content-agent-for-sell":
                                        return "Contact agent for price";
                                      case "price-guide-xyz-price":
                                        return `Price guide $${price}`;
                                      case "price-per-week":
                                        return `$${price}/Week`;
                                      case "price-per-month":
                                        return `$${price}/Month`;
                                      case "eoi":
                                        return `EOI ${moment(
                                          eoi_datetime
                                        ).format("MMMM D YYYY, hh:mm A")}`;
                                      default:
                                        return "";
                                    }
                                  })()}
                                </div>
                                <div className="date">
                                  <label>Listed on</label>{" "}
                                  {item.createdAt
                                    ? moment(item.createdAt).format(
                                        "MMMM D, YYYY"
                                      )
                                    : ""}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="modal-actions">
                    <button
                      onClick={() => {
                        setModalOpen(false);
                        setSelectedPropertyIds([]);
                        setSearchTerm("");
                      }}
                    >
                      Cancel
                    </button>
                    <button onClick={handleAddSelectedProperty}>Done</button>
                  </div>
                </>
              )}
            </div>
          </Modal>

          <Modal
            open={processingModalOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setModalOpen(false);
              }
            }}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="loading-content-wrapper">
              {isProcessing ? (
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request
                    <span className="desktop-break">
                      <br />
                    </span>
                    Please wait...
                  </p>
                </div>
              ) : null}
            </div>
          </Modal>

          {previewOpen && (
            <PreviewModal
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              images={previewImages}
            />
          )}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default OpenHomeDetailView;
